import { useMemo } from 'react';
import { useGetOrgModulesQuery } from '@/store/api/apiSlice';
import { PRODUCT_IDS, type ProductId } from '@/config/products';
import { parseTenantProducts, type TenantProductsMap } from '@/utils/productAccess';

type ModuleRow = {
  productId: ProductId;
  enabled: boolean;
  entitled: boolean;
  status?: string;
};

/** Server-resolved module access with JWT fallback while loading. */
export function useOrgModuleAccess(fallbackSettings?: Record<string, unknown> | null) {
  const { data, isLoading, isError, refetch } = useGetOrgModulesQuery(undefined);

  const productAccess = useMemo((): TenantProductsMap => {
    if (data?.products && typeof data.products === 'object') {
      return data.products as TenantProductsMap;
    }
    return parseTenantProducts(fallbackSettings);
  }, [data?.products, fallbackSettings]);

  const entitled = useMemo((): ProductId[] => {
    if (Array.isArray(data?.entitled)) return data.entitled as ProductId[];
    return PRODUCT_IDS.filter((id) => productAccess[id]?.enabled);
  }, [data?.entitled, productAccess]);

  const isEntitled = (productId: ProductId): boolean => {
    const row = (data?.modules as ModuleRow[] | undefined)?.find((m) => m.productId === productId);
    if (row) return row.entitled;
    return entitled.includes(productId);
  };

  return {
    productAccess,
    entitled,
    modules: (data?.modules as ModuleRow[] | undefined) ?? [],
    planTemplateId: data?.planTemplateId as string | undefined,
    isLoading,
    isError,
    refetch,
    isEntitled,
  };
}
